/**
 * SIS — Student Directory (rich list).
 * 14 columns: First Name | Last Name | Reg # | Class | Parent | Parent contact |
 *   Address | Health | DOB | Gender | Sponsorship | Catch-up | Attendance % | Active
 * Search, filter, sort, pagination, bulk-upload link, export, quick preview.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Search, Users, ChevronLeft, ChevronRight, Eye, FileDown, Upload, Filter, AlertCircle, FileText, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import ExportButton from '../components/ExportButton';
import BulkStudentImportDialog from '../components/BulkStudentImportDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAGE_SIZE = 25;

const SCHOOL_TITLES = {
  suubi_primary: 'Suubi Primary School',
  suubi_secondary: 'Suubi Secondary School',
};

const SISStudentsPage = ({ school }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    school: 'all', student_class: '', gender: 'all', sponsorship: 'all', catchup: 'all', is_active: 'all',
  });
  const [sort, setSort] = useState({ key: 'name', order: 'asc' });
  const [preview, setPreview] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: PAGE_SIZE, skip: page * PAGE_SIZE,
        sort: sort.key, order: sort.order,
      };
      if (search) params.search = search;
      if (filters.school !== 'all') params.school = filters.school;
      if (filters.student_class) params.student_class = filters.student_class;
      if (filters.gender !== 'all') params.gender = filters.gender;
      if (filters.sponsorship !== 'all') params.sponsorship = filters.sponsorship;
      if (filters.catchup !== 'all') params.catchup = filters.catchup;
      if (filters.is_active !== 'all') params.is_active = filters.is_active === 'active';
      const res = await axios.get(`${API_URL}/api/sis/students`, { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) { toast.error('Failed to load students'); }
    finally { setLoading(false); }
  }, [page, sort, search, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset to first page on filter/search change
  useEffect(() => { setPage(0); }, [search, filters, sort]);

  const splitName = (full) => {
    if (!full) return ['', ''];
    const parts = full.trim().split(/\s+/);
    return [parts[0] || '', parts.slice(1).join(' ')];
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const SortableTH = ({ field, children }) => (
    <TableHead
      className="cursor-pointer select-none hover:bg-muted/30"
      onClick={() => setSort({ key: field, order: sort.key === field && sort.order === 'asc' ? 'desc' : 'asc' })}
      data-testid={`sort-${field}`}
    >
      {children}
      {sort.key === field && <span className="ml-1 text-xs">{sort.order === 'asc' ? '↑' : '↓'}</span>}
    </TableHead>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Users className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-heading font-bold">{school ? SCHOOL_TITLES[school] : 'Student Directory'}</h1>
            <p className="text-sm text-muted-foreground">{total} student{total !== 1 && 's'} · Student Directory</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => navigate('/data-entry?form=student')} data-testid="sis-add-student-btn">
            + Add Student
          </Button>
          <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="sis-bulk-upload-btn">
            <Upload className="h-4 w-4 mr-1" /> Bulk Import
          </Button>
          <ExportButton resource="students" label="Export" />
        </div>
      </div>

      <Card>
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search name / reg # / parent..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="sis-search" />
          </div>
          {!school && (
            <Select value={filters.school} onValueChange={(v) => setFilters({ ...filters, school: v })}>
              <SelectTrigger data-testid="sis-school-filter"><SelectValue placeholder="School" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All schools</SelectItem>
                <SelectItem value="suubi_primary">Suubi Primary</SelectItem>
                <SelectItem value="suubi_secondary">Suubi Secondary</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Input placeholder="Class (e.g. P3)" value={filters.student_class} onChange={(e) => setFilters({ ...filters, student_class: e.target.value })} data-testid="sis-class-filter" />
          <Select value={filters.sponsorship} onValueChange={(v) => setFilters({ ...filters, sponsorship: v })}>
            <SelectTrigger data-testid="sis-sponsorship-filter"><SelectValue placeholder="Sponsorship" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sponsorship</SelectItem>
              <SelectItem value="enrolled">Sponsored</SelectItem>
              <SelectItem value="not_enrolled">Not sponsored</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.is_active} onValueChange={(v) => setFilters({ ...filters, is_active: v })}>
            <SelectTrigger data-testid="sis-active-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTH field="name">First Name</SortableTH>
                <TableHead>Last Name</TableHead>
                <SortableTH field="registration_number">Reg #</SortableTH>
                <SortableTH field="student_class">Class</SortableTH>
                <TableHead>Parent / Guardian</TableHead>
                <TableHead>Parent Contact</TableHead>
                <TableHead>Address</TableHead>
                <TableHead className="text-center">Health</TableHead>
                <SortableTH field="date_of_birth">DOB</SortableTH>
                <TableHead>Gender</TableHead>
                <TableHead>Sponsorship</TableHead>
                <TableHead>Catch-up</TableHead>
                <TableHead className="text-right">Attendance %</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={15} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={15} className="text-center py-8 text-muted-foreground">No students found</TableCell></TableRow>
              ) : items.map((s) => {
                const [first, last] = splitName(s.name);
                const parent = s.guardian_first_name ? `${s.guardian_first_name} ${s.guardian_last_name || ''}`.trim() : (s.parent_names || '—');
                const contact = s.guardian_phone || s.parent_contact || '—';
                const health = s.medical_conditions || s.allergies || s.disability_status;
                return (
                  <TableRow key={s.id} data-testid={`sis-row-${s.id}`} className="hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/sis/students/${s.id}`)}>
                    <TableCell className="font-medium">{first}</TableCell>
                    <TableCell>{last}</TableCell>
                    <TableCell className="font-mono text-xs">{s.registration_number || '—'}</TableCell>
                    <TableCell><Badge variant="outline">{s.student_class || '—'}</Badge></TableCell>
                    <TableCell className="text-sm">{parent}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{contact}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[180px] truncate" title={s.home_address}>{s.home_address || '—'}</TableCell>
                    <TableCell className="text-center">
                      {health ? <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">⚕ Note</Badge> : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-xs">{s.date_of_birth || '—'}</TableCell>
                    <TableCell className="text-xs capitalize">{s.gender || '—'}</TableCell>
                    <TableCell>{s.sponsorship_enrolled ? <Badge variant="outline" className="bg-pink-50 text-pink-700 border-pink-200">Sponsored</Badge> : <span className="text-muted-foreground text-xs">—</span>}</TableCell>
                    <TableCell>{s.catchup_enrolled ? <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">Catch-up</Badge> : <span className="text-muted-foreground text-xs">—</span>}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {s.attendance_pct != null ? (
                        <span className={s.attendance_pct >= 90 ? 'text-green-700 font-medium' : s.attendance_pct >= 75 ? 'text-amber-700' : 'text-red-700'}>{s.attendance_pct}%</span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>{(s.is_active !== false) ? <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Active</Badge> : <Badge variant="outline" className="bg-gray-100 text-gray-700">Inactive</Badge>}</TableCell>
                    <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="ghost" onClick={() => setPreview(s)} data-testid={`sis-preview-${s.id}`}><Eye className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/sis/students/${s.id}`)} data-testid={`sis-open-${s.id}`}>Open →</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex justify-between items-center text-sm">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="sis-prev-btn">
            <ChevronLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <span className="text-muted-foreground">Page {page + 1} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)} data-testid="sis-next-btn">
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      <BulkStudentImportDialog open={bulkOpen} onClose={() => setBulkOpen(false)} onImported={() => { setBulkOpen(false); fetchData(); }} />

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{preview?.name}</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-1 text-sm">
              <div><span className="text-muted-foreground">Reg #:</span> {preview.registration_number || '—'}</div>
              <div><span className="text-muted-foreground">Class:</span> {preview.student_class || '—'}</div>
              <div><span className="text-muted-foreground">DOB:</span> {preview.date_of_birth || '—'}</div>
              <div><span className="text-muted-foreground">Parent:</span> {preview.parent_names || `${preview.guardian_first_name || ''} ${preview.guardian_last_name || ''}`}</div>
              <div><span className="text-muted-foreground">Contact:</span> {preview.parent_contact || preview.guardian_phone || '—'}</div>
              <Button onClick={() => navigate(`/sis/students/${preview.id}`)} className="w-full mt-3" data-testid="preview-open-profile">Open full profile →</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SISStudentsPage;
